/**
 * Device Activation Screen — QR Primary with Fallback Code Entry
 *
 * Three UI states:
 *   State A (default): Welcome + Skandeer button + "probeer 'n ander manier" link
 *   State B (scanning): QR scanner active with viewfinder + "probeer 'n ander manier" link
 *   State C (fallback):  Existing code entry form + "skandeer QR-kode" link
 */

import { t } from '../i18n/config.js';
import { api } from '../services/api.js';
import { getDeviceId } from '../services/deviceAuth.js';
import { QrScanner } from '../components/QrScanner.js';
import {
  initPresenterMode,
  repositionBubble,
  restartFromScanQr,
  restartFromEnterCode,
  speakEnterCodeInstructions,
  speakScanQrInstructions,
} from '../utils/kassiePresenter.js';
import '../styles/qr-scanner.css';

/** @type {QrScanner|null} */
let scannerInstance = null;

/** @type {'stateA'|'stateB'|'stateC'} */
let currentState = 'stateA';

/**
 * Render device activation screen
 * @param {Object} options - Options for rendering
 * @param {boolean} options.revoked - Whether device was revoked
 */
export async function renderDeviceActivation(options = {}) {
  const app = document.getElementById('app');
  currentState = 'stateA';

  const revocationMessage = options.revoked
    ? `
    <div class="revocation-notice">
      <p class="revocation-text">⚠️ ${t('child.device.authorizationRevoked', 'This device is no longer authorized. Please contact your family administrator.')}</p>
    </div>
  `
    : '';

  app.innerHTML = `
    <div class="device-activation-container">
      <div class="activation-icon">
        <div class="activation-avatar-container">
          <img src="/svg/branding/KassieAvatarNoBorder.svg" alt="Kassie" class="activation-avatar">
        </div>
      </div>
      <div class="activation-card">
        ${revocationMessage}

        <!-- State A: Welcome + Skandeer button -->
        <div id="stateA" class="activation-state">
          <div class="activation-header">
            <h1>${t('child.device.welcome')}</h1>
          </div>

          <div class="activation-qr-section">
            <button id="startScanBtn" class="custom-button">
              <div class="bottom-div greenBG"></div>
              <div class="top-div kassieBG">
                <img class="icon-placeholder" src="/svg/buttons/QR.svg" alt="" width="20" height="20" />
                <span class="button-text">${t('child.device.scanButtonLabel')}</span>
              </div>
            </button>
            <p class="activation-qr-explanation">${t('child.device.scanButtonExplanation')}</p>
          </div>

          <div class="activation-method-switch">
            <button type="button" id="switchToCodeA" class="custom-button">
              <div class="bottom-div greenBG"></div>
              <div class="top-div kassieBG">
                <img class="icon-placeholder" src="/svg/buttons/Keypad.svg" alt="" width="20" height="20" />
                <span class="button-text">${t('child.device.useCode') || "Gebruik 'n kode"}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- State B: QR Scanner active -->
        <div id="stateB" class="activation-state" style="display: none;">
          <div id="qrScannerContainer" class="qr-scanner">
            <div class="qr-scanner__viewfinder qr-scanner__viewfinder--scanning">
              <div class="qr-scanner__scan-line"></div>
            </div>
          </div>
          <p class="activation-scanner-hint">${t('child.device.showQrToCamera')}</p>

          <div class="activation-method-switch">
            <button type="button" id="switchToCodeB" class="custom-button">
              <div class="bottom-div greenBG"></div>
              <div class="top-div kassieBG">
                <img class="icon-placeholder" src="/svg/buttons/Keypad.svg" alt="" width="20" height="20" />
                <span class="button-text">${t('child.device.useCode') || "Gebruik 'n kode"}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- State C: Fallback code entry -->
        <div id="stateC" class="activation-state" style="display: none;">
          <div class="activation-header">
            <h1>${t('child.device.welcomeToKassie')}</h1>
            <p>${t('child.device.enterCode')}</p>
          </div>

          <div class="activation-form">
            <div class="code-input-container">
              <div class="item">
                <input
                  type="text"
                  id="activationCode"
                  class="code-input"
                  placeholder="${t('child.device.enterCodePlaceholder')}"
                  maxlength="6"
                  pattern="[0-9]{6}"
                  inputmode="numeric"
                  autocomplete="off"
                >
              </div>
              <div class="item">
                <button id="activateBtn" class="custom-button" disabled>
                  <div class="bottom-div greenBG"></div>
                  <div class="top-div kassieBG">
                    <img class="icon-placeholder" src="/svg/buttons/Tick.svg" alt="" width="20" height="20" />
                    <span class="button-text">${t('child.actions.activate')}</span>
                  </div>
                </button>
              </div>
            </div>

            <div id="errorMessage" class="error-message" style="display: none;"></div>

            <div class="activation-help">
              <p>${t('child.device.codeInstructions')}</p>
            </div>
          </div>

          <div class="activation-method-switch">
            <button type="button" id="switchToQrC" class="custom-button">
              <div class="bottom-div greenBG"></div>
              <div class="top-div kassieBG">
                <img class="icon-placeholder" src="/svg/buttons/QR.svg" alt="" width="20" height="20" />
                <span class="button-text">${t('child.device.scanQrCodeLink')}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Bottom: code entry button (visible in states A and B) -->
        <div id="beginHierSection" class="activation-begin-hier">
        </div>
      </div>
    </div>
  `;

  bindActivationEvents();

  // Initialize presenter mode (Kassie replaces the static avatar and presents)
  initPresenterMode();
}

/**
 * Switch visible state panel
 * @param {'stateA'|'stateB'|'stateC'} newState
 */
function showState(newState) {
  const stateA = document.getElementById('stateA');
  const stateB = document.getElementById('stateB');
  const stateC = document.getElementById('stateC');
  const icon = document.querySelector('.activation-icon');
  const beginHier = document.getElementById('beginHierSection');

  stateA.style.display = 'none';
  stateB.style.display = 'none';
  stateC.style.display = 'none';

  if (newState === 'stateA') {
    stateA.style.display = '';
    if (icon) icon.style.display = '';
    if (beginHier) beginHier.style.display = '';
  } else if (newState === 'stateB') {
    stateB.style.display = '';
    if (icon) icon.style.display = 'none';
    if (beginHier) beginHier.style.display = 'none';
  } else if (newState === 'stateC') {
    stateC.style.display = '';
    if (icon) icon.style.display = '';
    if (beginHier) beginHier.style.display = '';
  }

  currentState = newState;

  // Switching state panels changes the card's height (e.g. "Use a code"
  // reveals the taller code-entry form), which re-centers the whole
  // activation-container flex column and moves the presenter avatar with
  // it. The speech bubble's position was a stale snapshot until this —
  // ask it to re-measure now instead of waiting for the next idle line.
  repositionBubble();
}

/**
 * Stop the QR scanner and release camera
 */
function stopScanner() {
  if (scannerInstance) {
    scannerInstance.stop();
    scannerInstance = null;
  }
}

/**
 * Start QR scanner — request camera and begin scanning
 */
async function startScanner() {
  showState('stateB');

  const container = document.getElementById('qrScannerContainer');

  scannerInstance = new QrScanner(container, handleQrResult, handleScannerError);
  await scannerInstance.start();
}

/**
 * Handle a successfully decoded QR result.
 * Extracts the auth token from the URL and redeems it.
 * @param {string} decodedText - Raw text decoded from QR code
 */
async function handleQrResult(decodedText) {
  try {
    const url = new URL(decodedText);
    const token = url.searchParams.get('auth');
    if (token) {
      await redeemToken(token);
    }
    // If no auth param, ignore — scanner already stopped via QrScanner component
  } catch {
    // Not a valid URL — ignore. Scanner already stopped; restart it.
    await startScanner();
  }
}

/**
 * Handle scanner errors (permission denied, no camera, etc.)
 * @param {'permission_denied'|'no_camera'|'unsupported'|'unknown'} errorType
 */
function handleScannerError(errorType) {
  stopScanner();

  if (errorType === 'permission_denied') {
    // Show permission denied message, then fallback to code entry
    showState('stateC');
    const errorMessage = document.getElementById('errorMessage');
    if (errorMessage) {
      errorMessage.textContent = `${t('child.device.cameraPermissionNeeded')}. ${t('child.device.cameraPermissionHelp')}`;
      errorMessage.style.display = 'block';
    }
  } else {
    // No camera or unsupported — go straight to code entry
    showState('stateC');
  }
}

/**
 * Redeem a device auth token (from QR scan or code entry)
 * @param {string} token - The auth token to redeem
 */
async function redeemToken(token) {
  try {
    const result = await api.post('/api/auth/device/redeem-token', {
      token,
    });

    if (result.authorized) {
      // Store device credentials
      const deviceAuth = { deviceId: result.deviceId, authorized: true };
      localStorage.setItem('deviceAuthorization', JSON.stringify(deviceAuth));
      localStorage.setItem(
        'authorizedFamily',
        JSON.stringify({
          id: result.familyId,
          name: result.familyName,
        })
      );
      localStorage.setItem('kassie_device_id', result.deviceId);

      // Park the handoff token so Add to Home Screen captures it
      const { parkHandoffToken } = await import('../utils/handoff.js');
      parkHandoffToken(result.handoffToken);

      // Show PWA install prompt after successful activation
      const { showPWAInstallPrompt } = await import('../utils/pwaInstall.js');
      showPWAInstallPrompt();

      // Success — reload to proceed to Profile Selection
      window.location.reload();
    } else {
      showTokenError(result.error || t('child.errors.invalidCode'));
    }
  } catch (error) {
    console.error('Token redemption error:', error);
    if (error.message && error.message.includes('expired')) {
      showTokenError(t('child.device.tokenExpired'));
    } else {
      showTokenError(t('child.device.connectionNeeded'));
    }
  }
}

/**
 * Display token error in the current state context
 * @param {string} message
 */
function showTokenError(message) {
  // Make sure we're in a state where the error can be shown — fallback to code state
  if (currentState === 'stateB') {
    showState('stateC');
  }
  const errorMessage = document.getElementById('errorMessage');
  if (errorMessage) {
    errorMessage.textContent = message;
    errorMessage.style.display = 'block';
  }
}

/**
 * Bind all activation event handlers
 */
function bindActivationEvents() {
  const startScanBtn = document.getElementById('startScanBtn');
  const switchToCodeA = document.getElementById('switchToCodeA');
  const switchToCodeB = document.getElementById('switchToCodeB');
  const switchToQrC = document.getElementById('switchToQrC');

  // Hovering either "Scan QR code" button restarts the presenter's speech
  // loop from that line, and either "Use a code" button restarts it from
  // "enterCode" — a no-op if the presenter isn't active (e.g. this markup
  // rendered but the device somehow already got authorized elsewhere).
  startScanBtn.addEventListener('mouseenter', restartFromScanQr);
  switchToQrC.addEventListener('mouseenter', restartFromScanQr);
  switchToCodeA.addEventListener('mouseenter', restartFromEnterCode);
  switchToCodeB.addEventListener('mouseenter', restartFromEnterCode);

  // State A: "Skandeer QR-kode" button → start scanner (State B)
  startScanBtn.addEventListener('click', async () => {
    speakScanQrInstructions();
    await startScanner();
  });

  // State A: "probeer 'n ander manier" → show code entry (State C)
  switchToCodeA.addEventListener('click', (e) => {
    e.preventDefault();
    stopScanner();
    showState('stateC');
    focusCodeInput();
    speakEnterCodeInstructions();
  });

  // State B: "probeer 'n ander manier" → stop camera, show code entry (State C)
  switchToCodeB.addEventListener('click', (e) => {
    e.preventDefault();
    stopScanner();
    showState('stateC');
    focusCodeInput();
    speakEnterCodeInstructions();
  });

  // State C: "skandeer QR-kode" → request camera again (State B)
  switchToQrC.addEventListener('click', async (e) => {
    e.preventDefault();
    speakScanQrInstructions();
    await startScanner();
  });

  // Bind code entry events (existing logic preserved)
  bindCodeEntryEvents();
}

/**
 * Focus the activation code input after a short delay
 */
function focusCodeInput() {
  setTimeout(() => {
    const codeInput = document.getElementById('activationCode');
    if (codeInput) codeInput.focus();
  }, 300);
}

/**
 * Bind event handlers for the code entry form.
 * Preserves all existing validation and submission logic.
 */
function bindCodeEntryEvents() {
  const codeInput = document.getElementById('activationCode');
  const activateBtn = document.getElementById('activateBtn');
  const errorMessage = document.getElementById('errorMessage');

  if (!codeInput || !activateBtn) return;

  // Enable/disable activate button based on input
  codeInput.addEventListener('input', (e) => {
    const code = e.target.value.replace(/\D/g, ''); // Only digits
    e.target.value = code;

    // Enable button when exactly 6 digits entered (backend requires min_length=6)
    activateBtn.disabled = code.length !== 6;

    // Clear error state
    codeInput.classList.remove('error');
    errorMessage.style.display = 'none';

    // Auto-submit when 6 digits entered
    if (code.length === 6) {
      setTimeout(() => activateBtn.click(), 500);
    }
  });

  // Handle paste event explicitly
  codeInput.addEventListener('paste', (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const code = pastedText.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    codeInput.value = code;

    // Enable button when exactly 6 digits pasted (backend requires min_length=6)
    activateBtn.disabled = code.length !== 6;

    // Clear error state
    codeInput.classList.remove('error');
    errorMessage.style.display = 'none';

    // Auto-submit when exactly 6 digits pasted
    if (code.length === 6) {
      setTimeout(() => activateBtn.click(), 500);
    }
  });

  // Handle activation
  activateBtn.addEventListener('click', async () => {
    const code = codeInput.value.trim();

    if (code.length !== 6) {
      showCodeError(t('child.errors.invalidCode'));
      return;
    }

    // Show loading state
    activateBtn.classList.add('loading');
    activateBtn.disabled = true;

    try {
      const deviceId = getDeviceId();
      const fp = {
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
      };
      // Log fingerprint to diagnostics for UA comparison debugging
      try {
        const { recDbg } = await import('../utils/recordingDebug.js');
        recDbg.log('AUTH: Sending fingerprint with activation code', {
          uaFirst100: fp.userAgent.substring(0, 100),
          screenWidth: fp.screenWidth,
          screenHeight: fp.screenHeight,
        });
      } catch { /* recDbg not ready */ }

      const result = await api.post('/api/auth/device/activation-code/validate', {
        code,
        deviceId,
        fingerprint: fp,
      });

      if (result.success) {
        // Store device authorization
        localStorage.setItem('deviceAuthorization', JSON.stringify(result.deviceAuthorization));
        localStorage.setItem('authorizedFamily', JSON.stringify(result.family));
        localStorage.setItem('kassie_device_id', deviceId);

        // Write to CacheStorage — this IS shared between Safari and PWA on iOS 14+
        try {
          if ('caches' in window) {
            const authData = JSON.stringify({
              deviceId,
              familyId: result.family.id,
              familyName: result.family.name,
              authorized: true,
            });
            const response = new Response(authData, { headers: { 'Content-Type': 'application/json' } });
            const cache = await caches.open('kassie-device-auth');
            await cache.put('/device-auth-state', response);
          }
        } catch { /* CacheStorage write failed — non-critical */ }

        // Set a JS cookie on app.kassie.media as additional fallback
        try {
          const cookieData = JSON.stringify({ deviceId, familyId: result.family.id, familyName: result.family.name });
          document.cookie = `kassie_device_js=${encodeURIComponent(cookieData)}; path=/; max-age=${365 * 24 * 60 * 60}; secure; samesite=lax`;
        } catch { /* cookie write failed — non-critical */ }

        // Log handoff mint result
        try {
          const { recDbg } = await import('../utils/recordingDebug.js');
          recDbg.log('AUTH: Activation response', { handoffMinted: result.handoffMinted, hasToken: !!result.handoffToken });
        } catch { /* recDbg not ready */ }

        // Park the handoff token so Add to Home Screen captures it
        const { parkHandoffToken } = await import('../utils/handoff.js');
        parkHandoffToken(result.handoffToken);

        // Show PWA install prompt after successful activation
        const { showPWAInstallPrompt } = await import('../utils/pwaInstall.js');
        showPWAInstallPrompt();

        // Success! Reload app to initialize main interface
        console.log('Device activated successfully for family:', result.family.id);
        window.location.reload();
      } else {
        showCodeError(result.error || t('child.errors.invalidCode'));
      }
    } catch (error) {
      console.error('Activation error:', error);
      if (error.message.includes('400')) {
        showCodeError(t('child.errors.invalidCode'));
      } else if (error.message.includes('expired')) {
        showCodeError(t('child.errors.codeExpired'));
      } else {
        showCodeError(t('child.errors.cannotConnect'));
      }
    } finally {
      // Remove loading state
      activateBtn.classList.remove('loading');
      activateBtn.disabled = codeInput.value.length !== 6;
    }
  });

  // Handle Enter key
  codeInput.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && !activateBtn.disabled) {
      activateBtn.click();
    }
  });

  /**
   * Show error in code entry form
   * @param {string} message
   */
  function showCodeError(message) {
    errorMessage.textContent = message;
    errorMessage.style.display = 'block';
    codeInput.classList.add('error');
    codeInput.focus();
    codeInput.select();
  }
}
